import React, { FunctionComponent, useContext, useEffect, useState } from "react";
import { ContentSection, getFileExtension, InfoMessage } from "sprint-common-web";

import { RecordType } from "../../../../../../common/types/RecordType";
import { VideoRekognitionResultProvider } from "../../../../../../content-ingestion/cloud-files/details/sections/preview/cloud-file-preview/rekognition/context/VideoRekognitionResultProvider";
import { SubtitlePreview } from "../../../../../../content-ingestion/cloud-files/details/sections/preview/cloud-file-preview/subtitle/SubtitlePreview";
import {
    FileType,
    setCloudFrontAuthCookie
} from "../../../../../../content-ingestion/cloud-files/details/sections/preview/cloud-file-preview/types/CloudFilePreviewItem";
import { VideoPreviewProvider } from "../../../../../../content-ingestion/cloud-files/details/sections/preview/cloud-file-preview/video/context/VideoPreviewProvider";
import { RekognitionVideoPreview } from "../../../../../../content-ingestion/cloud-files/details/sections/preview/cloud-file-preview/video/RekognitionVideoPreview";
import { VideoPreview } from "../../../../../../content-ingestion/cloud-files/details/sections/preview/cloud-file-preview/video/VideoPreview";
import { isZipFile } from "../../../../../../content-ingestion/common/isZipDvbFile";
import { AppConfigContext } from "../../../../../../core/configuration/Configuration";
import { AuthContext } from "../../../../../../core/security/AuthProvider";
import { CloudAssetDetails } from "../../../CloudAssetDetails";
import { useCloudAssetPreview } from "./useCloudAssetPreview";

interface CloudAssetDetailsFilePreviewProps {
    minHeight: number;
    assetDetails: CloudAssetDetails;
}

interface CloudFilePreviewItem {
    url: string;
    type: FileType;
}

export const CloudAssetDetailsFilePreview: FunctionComponent<CloudAssetDetailsFilePreviewProps> = ({
    assetDetails,
    minHeight
}: CloudAssetDetailsFilePreviewProps) => {
    const [filePreview, setFilePreview] = useState<CloudFilePreviewItem>({ url: "", type: FileType.Video });
    const [isPreviewLoading, setIsPreviewLoading] = useState<boolean>(true);
    const { sprintUser } = useContext(AuthContext);
    const { masterConfig } = useContext(AppConfigContext);

    const { data: cloudAssetPreview } = useCloudAssetPreview({
        contentVersionIds: assetDetails.content.versionSummary.ids,
        status: assetDetails.status,
        cloudFileId: assetDetails.cloudFileId,
        assetId: assetDetails.assetId
    });

    useEffect(() => {
        if (cloudAssetPreview) {
            setCloudFrontAuthCookie(masterConfig.cloudFrontDomain, sprintUser.accessToken);
            setFilePreview({ url: cloudAssetPreview.url, type: cloudAssetPreview.previewType });
            setIsPreviewLoading(false);
        }
    }, [cloudAssetPreview]);

    const renderPreview = () => {
        if (!assetDetails || !filePreview) return <></>;

        const { url, type } = filePreview;

        if (!url) {
            return <InfoMessage message={getNonPreviewInfoMessage(type)} />;
        }

        if (type === FileType.Subtitle) {
            const fileExt = getFileExtension(assetDetails.filePath);

            return <SubtitlePreview url={url} isZip={isZipFile(fileExt)} accessToken={sprintUser.accessToken} />;
        }

        if (assetDetails.type !== RecordType.Video) {
            return <VideoPreview cloudFileId={assetDetails.cloudFileId} manifestUrl={url} width="50vw" />;
        }

        return (
            <VideoRekognitionResultProvider cloudFileId={assetDetails.cloudFileId}>
                <RekognitionVideoPreview manifestUrl={url} cloudFileId={assetDetails.cloudFileId} />
            </VideoRekognitionResultProvider>
        );
    };

    return (
        <VideoPreviewProvider>
            <ContentSection
                header="Preview"
                style={{ minHeight: minHeight }}
                testId="cloud-file-preview-section"
                isLoading={isPreviewLoading}
            >
                {renderPreview()}
            </ContentSection>
        </VideoPreviewProvider>
    );
};

const getNonPreviewInfoMessage = (fileType: FileType) => {
    if (fileType === FileType.Video) {
        return "There is no audio asset associated to given version";
    }

    return "Preview for selected file type is not available";
};
